import { useEffect, useMemo, useState } from "react";
import { Shield, Download, Layers, FileCheck } from "lucide-react";
import api from "../lib/api";
import TopNav from "../components/TopNav";
import { useAuth } from "../lib/auth";

const TAB = { MITRE: "mitre", ISM: "ism" };

export default function Coverage() {
  const { user } = useAuth();
  const [tab, setTab] = useState(TAB.MITRE);
  const [mitre, setMitre] = useState(null);
  const [squadMitre, setSquadMitre] = useState(null);
  const [ism, setIsm] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLeader = user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, i] = await Promise.all([
          api.get("/coverage/mitre"),
          api.get("/coverage/ism"),
        ]);
        if (cancelled) return;
        setMitre(m.data);
        setIsm(i.data);
        if (isLeader) {
          const s = await api.get("/coverage/mitre/squad");
          if (!cancelled) setSquadMitre(s.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLeader]);

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="overline">// COVERAGE_MATRIX</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight mt-2">
            Mission Coverage
          </h1>
          <p className="text-[var(--text-2)] text-sm mt-3 max-w-3xl">
            Audit-grade evidence of training coverage mapped to MITRE ATT&amp;CK and the Australian Signals Directorate&apos;s
            Information Security Manual + Essential Eight. Exportable as a compliance pack for procurement and audit.
          </p>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <TabBtn id={TAB.MITRE} active={tab === TAB.MITRE} onClick={() => setTab(TAB.MITRE)} icon={Layers} label="MITRE ATT&CK" testId="tab-mitre" />
          <TabBtn id={TAB.ISM} active={tab === TAB.ISM} onClick={() => setTab(TAB.ISM)} icon={FileCheck} label="ISM + Essential Eight" testId="tab-ism" />
        </div>

        {loading && <div className="text-[var(--text-2)] font-mono text-sm">// Loading coverage data…</div>}

        {!loading && tab === TAB.MITRE && (
          <MitreSection mitre={mitre} squadMitre={squadMitre} isLeader={isLeader} />
        )}
        {!loading && tab === TAB.ISM && <IsmSection ism={ism} />}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`font-mono text-xs tracking-widest uppercase px-4 py-2 border inline-flex items-center gap-2 ${
        active ? "bg-[var(--cyan)] text-[var(--bg)] border-[var(--cyan)]" : "text-[var(--text-2)] border-[var(--border)] hover:text-[var(--cyan)] hover:border-[var(--cyan)]"
      }`}
    >
      <Icon size={12} strokeWidth={1.5} />{label}
    </button>
  );
}

/* -------------------- MITRE -------------------- */
function MitreSection({ mitre, squadMitre, isLeader }) {
  const techniquesByTactic = useMemo(() => {
    const grouped = {};
    Object.entries(mitre?.techniques || {}).forEach(([tid, row]) => {
      const tactic = row.tactic_id;
      if (!grouped[tactic]) grouped[tactic] = [];
      grouped[tactic].push({ id: tid, ...row });
    });
    return grouped;
  }, [mitre]);

  if (!mitre) return null;

  const tactics = mitre.tactics || [];

  // For leaders, surface squad-wide aggregates in the KPI strip — their personal
  // enrollment is always 0 and would otherwise read as broken on the demo.
  const showSquadKpis = isLeader && squadMitre;
  const kpiTiles = showSquadKpis
    ? [
        { label: "Squad size", value: squadMitre.squad_size, testId: "mitre-kpi-courses" },
        { label: "Unique techniques covered", value: Object.keys(squadMitre.technique_counts || {}).length, testId: "mitre-kpi-techniques", color: "var(--cyan)" },
        { label: "Trained operators", value: (squadMitre.members || []).filter((m) => m.trained_techniques.length > 0).length, testId: "mitre-kpi-tactics" },
      ]
    : [
        { label: "Completed courses", value: mitre.completed_course_count, testId: "mitre-kpi-courses" },
        { label: "Trained techniques", value: mitre.trained_technique_count, testId: "mitre-kpi-techniques", color: "var(--cyan)" },
        { label: "Tactics with coverage", value: Object.keys(techniquesByTactic).length, testId: "mitre-kpi-tactics" },
      ];

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        {kpiTiles.map((k) => (
          <Kpi key={k.label} label={k.label} value={k.value} color={k.color} testId={k.testId} />
        ))}
      </div>

      <div className="overline">// COVERAGE_BY_TACTIC</div>
      <div className="flat-card p-4 overflow-x-auto" data-testid="mitre-tactic-lanes">
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${tactics.length}, minmax(110px, 1fr))` }}>
          {tactics.map((t) => {
            const techs = techniquesByTactic[t.id] || [];
            return (
              <div key={t.id} className="border border-[var(--border)] p-2" data-testid={`tactic-${t.id}`}>
                <div className="font-mono text-[9px] tracking-widest uppercase text-[var(--text-2)] mb-2">{t.id}</div>
                <div className="font-display text-xs uppercase tracking-tight text-white mb-3 leading-tight">{t.name}</div>
                {techs.length === 0 ? (
                  <div className="font-mono text-[10px] text-[var(--text-2)] opacity-50">// no coverage</div>
                ) : (
                  <div className="space-y-1">
                    {techs.map((tk) => (
                      <div
                        key={tk.id}
                        title={`Trained by: ${tk.trained_by.join(", ")}`}
                        className="font-mono text-[10px] tracking-wide px-1.5 py-0.5 bg-[var(--cyan)] text-[var(--bg)]"
                      >
                        {tk.id}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isLeader && squadMitre && (
        <div>
          <div className="overline mt-8">// SQUAD_HEATMAP · {squadMitre.squad_size} OPERATORS</div>
          <div className="flat-card p-4 overflow-x-auto mt-2" data-testid="squad-heatmap">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr>
                  <th className="text-left text-[var(--text-2)] px-2 py-1">OPERATOR</th>
                  <th className="text-left text-[var(--text-2)] px-2 py-1">COURSES</th>
                  <th className="text-left text-[var(--text-2)] px-2 py-1">TECHNIQUES</th>
                </tr>
              </thead>
              <tbody>
                {squadMitre.members.map((m) => (
                  <tr key={m.user.id} className="border-t border-[var(--border)]" data-testid={`squad-row-${m.user.id}`}>
                    <td className="px-2 py-2 text-white">{m.user.name || m.user.email}</td>
                    <td className="px-2 py-2 text-[var(--text-2)]">{m.completed_course_count}</td>
                    <td className="px-2 py-2 text-[var(--cyan)]">{m.trained_techniques.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- ISM + E8 -------------------- */
function IsmSection({ ism }) {
  const csvUrl = useMemo(() => {
    if (!ism) return null;
    const rows = [["control_id", "description", "covered", "trained_by"]];
    Object.entries(ism.controls_catalog).forEach(([cid, desc]) => {
      const covered = ism.controls_covered[cid] || [];
      rows.push([cid, desc, covered.length > 0 ? "YES" : "NO", covered.join("|")]);
    });
    const csv = rows.map(r => r.map(cell => `"${String(cell).replaceAll('"', '""')}"`).join(",")).join("\n");
    return URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  }, [ism]);

  if (!ism) return null;
  const e8 = ism.essential_eight || [];

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        <Kpi label="ISM controls covered" value={`${ism.covered_count} / ${ism.total_controls}`} color="var(--cyan)" testId="ism-kpi-controls" />
        <Kpi label="E8 strategies at M2+" value={e8.filter(s => s.maturity >= 2).length} testId="ism-kpi-e8-m2" color="var(--amber)" />
        <Kpi label="E8 strategies at M3" value={e8.filter(s => s.maturity >= 3).length} testId="ism-kpi-e8-m3" color="var(--green)" />
      </div>

      <div className="flex items-center justify-between">
        <div className="overline">// ESSENTIAL_EIGHT_MATURITY</div>
        <a href={csvUrl} download="defdossier-compliance-export.csv" className="font-mono text-xs tracking-widest uppercase text-[var(--cyan)] hover:underline inline-flex items-center gap-1" data-testid="export-csv">
          <Download size={12} /> Export CSV
        </a>
      </div>

      <div className="flat-card p-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="e8-grid">
          {e8.map((s) => (
            <div key={s.id} className="border border-[var(--border)] p-3" data-testid={`e8-${s.id}`}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] mb-1">{s.id}</div>
              <div className="font-display text-sm uppercase tracking-tight text-white mb-3 leading-tight">{s.name}</div>
              <div className="flex items-center gap-1">
                {[1, 2, 3].map((lvl) => (
                  <div
                    key={lvl}
                    className="h-2 flex-1"
                    style={{
                      background: s.maturity >= lvl
                        ? (lvl === 3 ? "var(--green)" : lvl === 2 ? "var(--cyan)" : "var(--amber)")
                        : "var(--border)",
                    }}
                  />
                ))}
                <span className="font-mono text-[10px] tracking-widest uppercase ml-1" style={{ color: s.maturity > 0 ? "var(--cyan)" : "var(--text-2)" }}>
                  M{s.maturity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="overline mt-8">// ISM_CONTROL_COVERAGE</div>
      <div className="flat-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--border)]">
            <tr className="text-left font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">
              <th className="px-4 py-2">Control</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Trained By</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(ism.controls_catalog).map(([cid, desc]) => {
              const trained = ism.controls_covered[cid] || [];
              const covered = trained.length > 0;
              return (
                <tr key={cid} className="border-t border-[var(--border)]" data-testid={`ism-row-${cid}`}>
                  <td className="px-4 py-2 font-mono text-xs text-[var(--cyan)]">{cid}</td>
                  <td className="px-4 py-2 text-[var(--text)]">{desc}</td>
                  <td className="px-4 py-2">
                    <Shield size={14} strokeWidth={1.5} style={{ color: covered ? "var(--green)" : "var(--text-2)" }} />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-[var(--text-2)]">
                    {covered ? trained.join(", ") : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, color = "var(--text)", testId }) {
  return (
    <div className="flat-card p-4" data-testid={testId}>
      <div className="overline mb-1">{label}</div>
      <div className="font-display font-bold text-3xl" style={{ color }}>{value}</div>
    </div>
  );
}
